<template>
  <div
    ref="containerRef"
    class="pull-to-refresh"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
  >
    <!-- Indicator dat naar beneden komt -->
    <div
      class="pull-to-refresh__indicator"
      :class="{
        'pull-to-refresh__indicator--visible': pullDistance > 0 || isRefreshing,
        'pull-to-refresh__indicator--ready': pullDistance >= threshold,
        'pull-to-refresh__indicator--refreshing': isRefreshing,
      }"
      :style="{ height: `${Math.min(pullDistance, threshold)}px` }"
    >
      <div class="pull-to-refresh__spinner">
        <LoadingSpinner v-if="isRefreshing" size="md" color="brand" />
        <BaseIcon
          v-else
          name="arrow-down"
          :class="{ 'pull-to-refresh__icon--rotate': pullDistance >= threshold }"
          size="20"
        />
      </div>
      <span class="pull-to-refresh__text">
        {{ isRefreshing ? refreshingText : pullDistance >= threshold ? releaseText : pullText }}
      </span>
    </div>

    <!-- Content -->
    <div class="pull-to-refresh__content">
      <slot></slot>
    </div>
  </div>
</template>

<script>
  import { defineComponent, ref } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';

  export default defineComponent({
    name: 'PullToRefresh',
    components: {
      BaseIcon,
      LoadingSpinner,
    },
    props: {
      onRefresh: {
        type: Function,
        required: true,
      },
      threshold: {
        type: Number,
        default: 60,
      },
      pullText: {
        type: String,
        default: 'Pull to refresh',
      },
      releaseText: {
        type: String,
        default: 'Release to refresh',
      },
      refreshingText: {
        type: String,
        default: 'Refreshing...',
      },
    },
    setup(props) {
      const containerRef = ref(null);
      const isRefreshing = ref(false);
      const pullDistance = ref(0);
      const isPulling = ref(false);

      let startY = 0;
      let canPull = false;

      const isMobile = () => {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      };

      const getScrollContainer = () => {
        return document.querySelector('.app-wrapper');
      };

      const getScrollTop = () => {
        const scrollContainer = getScrollContainer();
        if (scrollContainer) {
          return scrollContainer.scrollTop;
        }

        return window.scrollY || document.documentElement.scrollTop || 0;
      };

      const hasScrollableParentWithOffset = (target) => {
        if (!target || !(target instanceof Element)) {
          return false;
        }

        let current = target;
        while (current && current !== containerRef.value && current !== document.body) {
          const styles = window.getComputedStyle(current);
          const canScrollY = /(auto|scroll)/.test(styles.overflowY);
          if (canScrollY && current.scrollHeight > current.clientHeight && current.scrollTop > 0) {
            return true;
          }
          current = current.parentElement;
        }

        return false;
      };

      const handleTouchStart = (e) => {
        if (!isMobile() || isRefreshing.value) return;

        if (hasScrollableParentWithOffset(e.target)) {
          canPull = false;
          return;
        }

        // Only allow pull if we're at the top
        if (getScrollTop() <= 0) {
          startY = e.touches[0].clientY;
          canPull = true;
        } else {
          canPull = false;
        }
      };

      const handleTouchMove = (e) => {
        if (!canPull || isRefreshing.value) return;

        const currentY = e.touches[0].clientY;
        const distance = currentY - startY;

        if (distance > 0 && getScrollTop() <= 0) {
          isPulling.value = true;
          pullDistance.value = Math.min(distance * 0.4, props.threshold * 1.5);

          // Prevent browser's native behavior
          if (distance > 5 && e.cancelable) {
            e.preventDefault();
          }
        } else if (distance <= 0) {
          isPulling.value = false;
          pullDistance.value = 0;
        }
      };

      const handleTouchEnd = async () => {
        if (!isPulling.value) {
          canPull = false;
          return;
        }

        canPull = false;

        if (pullDistance.value >= props.threshold && props.onRefresh) {
          isRefreshing.value = true;
          isPulling.value = false;

          try {
            await props.onRefresh();
          } catch (error) {
            console.error('Pull to refresh error:', error);
          } finally {
            isRefreshing.value = false;
          }
        }

        isPulling.value = false;
        pullDistance.value = 0;
        startY = 0;
      };

      return {
        containerRef,
        isRefreshing,
        pullDistance,
        isPulling,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
      };
    },
  });
</script>

<style lang="scss" scoped>
  .pull-to-refresh {
    position: relative;
    min-height: 100%;
    // Disable browser's native pull-to-refresh
    overscroll-behavior-y: contain;

    &__indicator {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      height: 0;
      overflow: hidden;
      background: var(--slate-50, #f8fafc);
      transition: none;

      &--refreshing {
        height: 60px !important;
        transition: height 0.2s ease;
      }
    }

    &__spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      background: white;
      border-radius: 50%;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      color: var(--slate-600);
    }

    &__icon--rotate {
      transform: rotate(180deg);
      transition: transform 0.2s ease;
    }

    &__text {
      font-size: 0.8125rem;
      color: var(--slate-500);
      font-weight: 500;
    }

    &__content {
      // Content wrapper
    }
  }
</style>
